import React from 'react';
import {
	Dialog,
	DialogContent,
	DialogActions,
	Button,
	IconButton,
	Box,
	Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Close } from '@material-ui/icons';
import AppShowcase from 'app/main/landing-page/components/AppShowcase';
import ShortcodeParser from 'app/shared-components/ShortcodeParser';

const useStyles = makeStyles(theme => ({
	// Default dialog styling (for AppShowcase component)
	dialog: {
		'& .MuiDialog-paper': {
			backgroundColor: '#0f172a',
			borderRadius: '12px',
			border: '1px solid #334155',
			maxWidth: '1200px',
			width: '100%',
			maxHeight: '90vh',
			margin: theme.spacing(2)
		}
	},
	// Transparent dialog (for custom HTML)
	dialogTransparent: {
		'& .MuiDialog-paper': {
			backgroundColor: 'transparent',
			boxShadow: 'none',
			border: 'none',
			borderRadius: 0,
			maxWidth: '1200px',
			width: 'auto',
			maxHeight: '95vh',
			margin: 'auto',
			overflow: 'visible',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center'
		},
		'& .MuiBackdrop-root': {
			backgroundColor: 'rgba(0, 0, 0, 0.15)'
		}
	},
	header: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		padding: '16px 24px',
		borderBottom: '1px solid #334155'
	},
	title: {
		fontSize: '18px',
		fontWeight: 600,
		color: '#f8fafc',
		display: 'flex',
		alignItems: 'center',
		gap: '8px'
	},
	dialogContent: {
		padding: 0,
		overflow: 'hidden',
		position: 'relative',
		backgroundColor: '#0f172a'
	},
	dialogContentTransparent: {
		padding: 0,
		overflow: 'visible',
		position: 'relative',
		backgroundColor: 'transparent'
	},
	closeButton: {
		position: 'absolute',
		right: theme.spacing(1),
		top: theme.spacing(1),
		zIndex: 2000,
		backgroundColor: 'rgba(15, 23, 42, 0.95)',
		color: '#94a3b8',
		border: '1px solid #334155',
		'&:hover': {
			backgroundColor: 'rgba(30, 41, 59, 0.95)',
			borderColor: '#475569'
		}
	},
	closeButtonTransparent: {
		position: 'absolute',
		right: theme.spacing(2),
		top: theme.spacing(2),
		zIndex: 2000,
		backgroundColor: 'rgba(0, 0, 0, 0.8)',
		color: '#ffffff',
		border: '1px solid rgba(255, 255, 255, 0.2)',
		'&:hover': {
			backgroundColor: 'rgba(0, 0, 0, 0.95)',
			borderColor: 'rgba(255, 255, 255, 0.4)'
		}
	},
	customHtmlContainer: {
		width: '100%',
		minHeight: '400px',
		'& img': {
			maxWidth: '100%',
			height: 'auto'
		},
		'& a': {
			color: theme.palette.primary.main,
			textDecoration: 'none',
			'&:hover': {
				textDecoration: 'underline'
			}
		}
	},
	dialogActions: {
		padding: theme.spacing(2),
		justifyContent: 'center',
		borderTop: '1px solid #334155',
		backgroundColor: '#0f172a'
	},
	getStartedButton: {
		backgroundColor: theme.palette.primary.main,
		color: '#ffffff',
		padding: '12px 32px',
		fontSize: '16px',
		fontWeight: 600,
		borderRadius: '8px',
		textTransform: 'none',
		'&:hover': {
			backgroundColor: theme.palette.primary.dark
		}
	}
}));

function CampaignShowcaseDialog({ open, onClose, campaignData }) {
	const classes = useStyles();

	if (!campaignData) {
		return null;
	}

	const hasCustomHtml = campaignData.customShowcaseHtml && campaignData.customShowcaseHtml.trim() !== '';
	const isTransparent = hasCustomHtml;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			className={isTransparent ? classes.dialogTransparent : classes.dialog}
			maxWidth={false}
			aria-labelledby="campaign-showcase-dialog"
		>
			<IconButton
				aria-label="close"
				className={isTransparent ? classes.closeButtonTransparent : classes.closeButton}
				onClick={onClose}
			>
				<Close />
			</IconButton>

			<DialogContent className={isTransparent ? classes.dialogContentTransparent : classes.dialogContent}>
				{hasCustomHtml ? (
					// Render custom HTML content with shortcode parsing
					<ShortcodeParser htmlContent={campaignData.customShowcaseHtml} />
				) : (
					// Render default AppShowcase component
					<AppShowcase
						config={campaignData.landingPageConfig || {}}
						branding={campaignData.customBranding || {}}
					/>
				)}
			</DialogContent>

			{!isTransparent && (
				<DialogActions className={classes.dialogActions}>
					<Button
						onClick={onClose}
						className={classes.getStartedButton}
						size="large"
					>
						Get Started
					</Button>
				</DialogActions>
			)}
		</Dialog>
	);
}

export default CampaignShowcaseDialog;
